/**
 * Durable record of the editing sessions THIS BROWSER holds — BUG-146069-005.
 *
 * BUG-146069-001 made the pre-logout release outlive the editor COMPONENT, by moving the
 * registration into a module-scoped `Map` keyed by session id. That was the right diagnosis
 * (the backend session and its exclusive `DocumentLock` deliberately survive unmount, so the
 * release had to as well) and it stopped one step short: a module-scoped `Map` does not survive
 * a full page load, and the backend session does.
 *
 * |                                         | Survives unmount | Survives a page load |
 * |-----------------------------------------|------------------|----------------------|
 * | Backend `EditingSession` + `DocumentLock` | yes (Story 72b.2) | yes                |
 * | `sessionReleaseRegistry`'s `Map`          | yes (the 001 fix) | **no**             |
 * | `activeEditorSession` localStorage marker | no — cleared on unmount | n/a          |
 *
 * So the broken sequence is: leave the editor (Back, or any in-app nav), take any full page load
 * (F5, a pasted URL, or the shell's own `window.location.reload()` behind an MFE error boundary's
 * Retry), then sign out. The registry is empty, the batch is empty, and the backend backstop
 * excludes the session because its heartbeat stopped inside the 300s `LogoutSessionGraceSeconds`
 * window — the same gate that made 001 necessary, reached by one extra step. A refresh taken from
 * INSIDE the editor is fine: the `?doc=` restore path re-mounts the editor, which re-registers.
 *
 * This module is the durable half. The editor records every session it acquires; this module
 * registers ONE pre-logout task that reads the record at sign-out time and closes whatever is
 * still listed. Reading at sign-out rather than rehydrating at boot is deliberate — there is then
 * no ordering dependency between "the record was written" and "the app booted", and one
 * registration covers every entry however it got there.
 *
 * ## Why this lives in shared-domain rather than in the projects remote
 *
 * The whole point is to survive a page load that lands on ANY route. The projects remote is
 * lazily loaded, so a reload onto the dashboard would never evaluate a module living there. This
 * package is eagerly loaded by the shell, which is the only placement that covers every landing
 * route. The cost is that shared-domain names one document-service endpoint; it already calls the
 * Agent and notification services directly (`agentV2Api.service.ts`, `milestoneBannerClient.ts`),
 * so this is the established shape rather than a new dependency direction.
 *
 * ## Module-federation contract
 *
 * Consumers MUST import from `@helios/shared-domain/stores` (the barrel), never by a deep path —
 * the same exact-match share-key rule `preLogoutTasks` documents, and for the same reason: a deep
 * import hands the remote a private copy of `liveSessions`, whose whole job is to be shared.
 */

import axiosInstance from '../api/axiosInstance';
import { getResourceTenantId, RESOURCE_TENANT_HEADER } from '../api/resourceTenantProvider';
import { registerPreLogoutTask } from './preLogoutTasks';

const STORAGE_KEY = 'helios-held-editing-sessions';

/**
 * Ceiling on how long a record is worth acting on, in ms.
 *
 * Matches the Document service's committed `SessionMonitor:SessionTimeoutMinutes` default (30),
 * which is how long a `DocumentLock` lives before `CleanupExpiredLocks` can reclaim it. Past
 * that there is nothing this browser can usefully release, and keeping the entry would put a
 * pointless `/close` on every sign-out forever.
 *
 * Deliberately generous rather than exact: the daemon's idle transition REFRESHES a lock rather
 * than releasing it (`SessionMonitorDaemon`, GAP-9), so a real lock can outlive this window. An
 * entry we drop too early costs a release the idle sweep still performs; an entry we keep forever
 * costs every future sign-out. The first is the cheaper mistake.
 */
export const HELD_SESSION_MAX_AGE_MS = 30 * 60 * 1000;

/** One editing session this browser acquired and has not confirmed releasing. */
export interface HeldEditingSession {
  /** The Document service editing-session id — all `/close` needs. */
  sessionId: string;
  /**
   * The `X-Resource-Tenant-Id` in force when the lock was acquired, or null for the JWT default.
   *
   * Captured rather than re-read at sign-out: a session's `TenantId` is the ACTIVE resource
   * tenant, and the repository read filters on it, so a user who switched client after navigating
   * away would otherwise send the close under the wrong tenant and get a 404 — releasing nothing
   * while looking like it had tried.
   */
  resourceTenantId: string | null;
  /** Epoch ms, for the staleness bound above. */
  acquiredAt: number;
}

/**
 * Sessions a LIVE in-page registration already covers (`sessionReleaseRegistry` in the projects
 * remote), so the durable task skips them.
 *
 * Without this the common case double-closes: reload back INTO the editor and both the re-mounted
 * editor's registration and this module's record name the same session. Two concurrent `/close`
 * calls race for the session's lifecycle claim, so the loser answers 409 `Deferred` — which the
 * outcome classifier correctly reads as "the locks are STILL HELD" and logs as the abandoned-lock
 * symptom. A false alarm on exactly the signal that exists to make the real thing findable.
 *
 * In-memory on purpose: it describes what is registered in THIS page's memory, which is precisely
 * what a page load resets.
 */
const liveSessions = new Set<string>();

let initialized = false;

function read(): HeldEditingSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is HeldEditingSession =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as HeldEditingSession).sessionId === 'string' &&
        typeof (entry as HeldEditingSession).acquiredAt === 'number'
    );
  } catch {
    // Unavailable (private mode, disabled site data) or corrupt. Either way there is nothing to
    // release and nothing worth failing a sign-out over.
    return [];
  }
}

function write(entries: HeldEditingSession[]): void {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota or unavailable storage — non-critical. The in-page registry remains the primary
    // release path; this record is the backstop for the page loads that erase it.
  }
}

/**
 * Records that this browser holds `sessionId`, so a page load cannot erase the fact.
 *
 * The active resource tenant is read HERE rather than passed in: the caller holds a session id
 * and nothing else, and which tenant header the release must carry is this module's problem.
 *
 * Idempotent per session: re-recording refreshes the tenant but KEEPS the original `acquiredAt`,
 * so the staleness bound measures the lock's real age rather than being reset by every re-mount.
 */
export function recordHeldEditingSession(sessionId: string): void {
  const existing = read();
  const prior = existing.find((entry) => entry.sessionId === sessionId);
  write([
    ...existing.filter((entry) => entry.sessionId !== sessionId),
    {
      sessionId,
      resourceTenantId: getResourceTenantId(),
      acquiredAt: prior?.acquiredAt ?? Date.now(),
    },
  ]);
}

/**
 * Drops the record for `sessionId`.
 *
 * Call this ONLY where the release is confirmed — the same rule, and the same reason, as
 * `unregisterSessionRelease` (BUG-146069-004): dropping it on the attempt is what leaves a held
 * lock with nothing left to release it.
 */
export function forgetHeldEditingSession(sessionId: string): void {
  const remaining = read().filter((entry) => entry.sessionId !== sessionId);
  write(remaining);
}

/** Every recorded session still young enough to be worth releasing. Prunes what is not. */
export function readHeldEditingSessions(): HeldEditingSession[] {
  const all = read();
  const cutoff = Date.now() - HELD_SESSION_MAX_AGE_MS;
  const fresh = all.filter((entry) => entry.acquiredAt >= cutoff);
  if (fresh.length !== all.length) write(fresh);
  return fresh;
}

/** Tells this module that a live in-page registration covers `sessionId`. */
export function markHeldSessionLive(sessionId: string): void {
  liveSessions.add(sessionId);
}

/** Tells this module that no live in-page registration covers `sessionId` any more. */
export function unmarkHeldSessionLive(sessionId: string): void {
  liveSessions.delete(sessionId);
}

/**
 * Close one recorded session, and forget it only if the answer says the lock is gone.
 *
 * The retention rule mirrors `sessionReleaseOutcome.shouldRetainRelease` in the projects remote,
 * which classifies the same statuses more richly for the in-page path. The two MUST agree, and
 * the split is deliberate: that module reads response bodies the editor cares about, while all
 * this one needs is whether to keep trying.
 *
 * **The rejection shapes are NOT the same, and reading the wrong one is silent.** That module
 * classifies an orval `customFetch` rejection, where the status sits at the TOP level
 * (`{ status, data }`); this one classifies an **axios** rejection, where it is nested
 * (`error.response.status`). Reading `err.status` here would be `undefined` for every outcome,
 * collapsing a 409 into "unknown" — the exact trap `sessionReleaseOutcome`'s own doc block
 * catalogues, one error shape along.
 *
 *  - any 2xx — released (`closed`), or `alreadyClosed`, which is a routine no-op. Forget.
 *  - 404 — the session is gone. Forget.
 *  - 403 — we may not release it (not ours, or credentials already down). Retrying would answer
 *    403 again, so forget rather than strand an entry that can never succeed. This is the arm
 *    BUG-146069-006 makes reachable.
 *  - 409 `SESSION_CLOSE_DEFERRED` — the backend's own word that the locks are STILL HELD. Keep.
 *  - anything else, including a transport failure with no status — unknown. Keep.
 *
 * Never throws: sign-out must not depend on a release landing.
 */
async function closeHeldSession(entry: HeldEditingSession): Promise<void> {
  try {
    await axiosInstance.post(
      `/api/v2/sessions/${encodeURIComponent(entry.sessionId)}/close`,
      undefined,
      entry.resourceTenantId
        ? { headers: { [RESOURCE_TENANT_HEADER]: entry.resourceTenantId } }
        : undefined
    );
    forgetHeldEditingSession(entry.sessionId);
    console.debug(`[BUG-146069] session ${entry.sessionId}: released from the durable record.`);
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status ?? null;
    if (status === 403 || status === 404) {
      forgetHeldEditingSession(entry.sessionId);
      console.warn(
        `[BUG-146069] session ${entry.sessionId}: durable release answered ${status} — nothing ` +
          'left for this browser to hand back, dropping the record.'
      );
      return;
    }
    if (status === 409) {
      console.error(
        `[BUG-146069] session ${entry.sessionId}: durable release DEFERRED (409) — the document ` +
          'is STILL LOCKED for other users until the session monitor daemon completes it.'
      );
      return;
    }
    console.warn(
      `[BUG-146069] session ${entry.sessionId}: durable release did not confirm a release ` +
        `(status ${status ?? 'none'}). The record is kept so the next sign-out retries it.`
    );
  }
}

/**
 * Registers the single pre-logout task that releases every durably-recorded session.
 *
 * Idempotent, and chained from `initializeAuthSync()` — which every tab runs on mount — for the
 * same reason `initializePreLogoutPeers` is: it is the one place guaranteed to run once per page
 * load regardless of which route the load landed on.
 *
 * The task runs inside `runPreLogoutTasks()`, which `useAuthStore.logout()` awaits BEFORE the
 * `/api/auth/logout` POST and `clearAuthState()`. That ordering is load-bearing twice over here:
 * credentials are still valid, and `clearAuthState()` wipes localStorage whole (it preserves only
 * an explicit key list), so a record read any later would already be gone.
 */
export function initializeHeldEditingSessionReleases(): void {
  if (initialized) return;
  initialized = true;

  registerPreLogoutTask(async () => {
    const pending = readHeldEditingSessions().filter((entry) => !liveSessions.has(entry.sessionId));
    if (pending.length === 0) return;
    await Promise.all(pending.map(closeHeldSession));
  });
}

/**
 * Test seam: drops every record and re-arms the initializer so suites cannot leak into each
 * other. Pair it with `clearPreLogoutTasks()` — the task this module registered lives in the
 * shared batch, which owns its own reset.
 */
export function resetHeldEditingSessions(): void {
  liveSessions.clear();
  initialized = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
